<template>
    <div>
        <b-modal
            :visible="value"
            :id="modal_id"
            :size="edit_mode ? 'xl' : 'lg'"
            @cancel="cancel"
            @close="cancel"
            @hidden="hidden"
            @ok="approveNarration"
            @shown="initializeAudioHighlighting"
        >
            <template #modal-title>
                Narration Audio Sync
            </template>

            <div class="mb-3 d-flex">
                <audio
                    id="audio-player"
                    ref="audio-player"
                    class="d-flex mb-0"
                    controls
                    @play="startTick"
                    @pause="endTick"
                >
                    <source :src="narration.url" type="audio/mpeg">
                    Your browser does not support the audio element.
                </audio>
            </div>
            <div class="row">
                <div :class="{
                    'col-12': !edit_mode,
                    'col-6': edit_mode,
                }">
                    <h5 v-if="edit_mode">Content Body</h5>
                    <div class="mb-3">
                        <div v-if="parsed_article">
                            <div v-html="parsed_article" class="v-html parsed-article" @click="checkTokenClicked" />
                        </div>
                    </div>
                </div>
                <div v-if="edit_mode" class="col-6">
                    <div class="d-flex mb-2">
                        <h5>Timestamps</h5>
                        <div class="flex-grow-1 d-flex justify-content-end mr-2">
                            <b-btn size="sm" @click="updateAudioTimes" :disabled="saving">
                                <font-awesome-icon
                                    :icon="['fas', 'save']"
                                    fixed-width
                                />
                            </b-btn>
                        </div>

                    </div>
                    <div class="mb-3 px-2 timestamps-editor">
                        <b-table-simple>
                            <b-thead>
                                <b-th style="width: 30%"></b-th>
                                <b-th style="width: 40%">Word</b-th>
                                <b-th style="width: 30%">Time</b-th>
                            </b-thead>
                            <b-tr v-for="(audio_time, index) in audio_times" :key="index">
                                <b-td class="timestamp-actions">
                                    <b-btn size="sm" class="mr-2" variant="outline-info" @click="jumpToTime(index)">
                                        <font-awesome-icon
                                            :icon="['fas', 'location-circle']"
                                            fixed-width
                                        />
                                    </b-btn>
                                    <b-btn
                                        size="sm"
                                        class="mr-2"
                                        variant="outline-primary"
                                        @click="jumpToTime(index, true, true)"
                                    >
                                        <font-awesome-icon
                                            :icon="['fas', 'play']"
                                            fixed-width
                                        />
                                    </b-btn>
                                    <b-btn 
                                        size="sm" 
                                        variant="outline-danger" 
                                        class="mr-2"
                                        @click="removeWord(index)">
                                        <font-awesome-icon
                                            :icon="['fas', 'trash']"
                                            fixed-width
                                        />
                                    </b-btn>
                                    <b-btn 
                                        size="sm" 
                                        variant="outline-success" 
                                        @click="insertWord(index)">
                                        <font-awesome-icon
                                            :icon="['fas', 'plus']"
                                            fixed-width
                                        />
                                    </b-btn>
                                </b-td>
                                <b-td>
                                    <b-form-input
                                        type="text"
                                        :value="audio_time.word"
                                        @change="(event) => adjustWord(event, index)"
                                    />
                                </b-td>
                                <b-td>
                                    <b-form-input
                                        type="number"
                                        :value="audio_time.time"
                                        @input="(event) => adjustTime(event, index)"
                                        step="100"
                                    />
                                </b-td>
                            </b-tr>
                        </b-table-simple>
                    </div>
                </div>
            </div>

            <template #modal-footer>
                <b-form-checkbox
                    v-model="follow_highlight"
                    class="mr-2"
                    name="highlight-button"
                    switch
                >
                    Follow Highlighter
                </b-form-checkbox>
                <b-form-checkbox
                    v-if="can_update_narration"
                    v-model="edit_mode"
                    class="mr-2"
                    name="mode-button"
                    switch
                >
                    Edit Mode
                </b-form-checkbox>
                <b-button
                    variant="light"
                    @click="cancel"
                >
                    Cancel
                </b-button>
                <b-button
                    v-if="narration.status !== `Audio QC'd` && narration.status !== 'Audio Processing'"
                    variant="success"
                    @click="approveNarration"
                >
                    <font-awesome-icon :icon="['fad', 'check']" fixed-width />
                    Approve Audio Sync
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<style lang="scss" scoped>
.parsed-article {
    overflow-y: scroll;
    max-height: 300px;
}

.col-6 {
    .parsed-article, .timestamps-editor {
        overflow-y: scroll;
        max-height: 65vh;
    }
}

.timestamp-actions {
    white-space: nowrap;
}

</style>

<style lang="scss">
.token {
    cursor: pointer;
    user-select: none;
}

#highlight {
    color: #FFFFFF;
    background-color: #896DA2;
    border: 1px solid #8667A7;
    margin: -1px;
    border-radius: 4px;
}
</style>

<script>
import {mapState} from 'vuex'
import moment from 'moment'

export default {
    data: () => ({
        audio_times: null,
        audio_player: null,
        audio_interval: null,
        speed_modifier: 1,
        edit_mode: false,
        follow_highlight: false,
        saving: false,
        play_until: null,
    }),
    props: [
        'value',
        'article',
        'narration'
    ],
    components: {},
    computed: {
        ...mapState({
            user: state => state.user.user,
        }),
        modal_id() {
            let modal_id = 'narration-audio-sync-modal'

            return modal_id
        },
        parsed_article() {
            let parsed_article = null

            if (this.narration.parsed_article) {
                parsed_article = this.narration.parsed_article
            }

            return parsed_article
        },
        can_update_narration() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_narration')
        },
    },
    methods: {
        async updateAudioTimes() {
            if (!this.narration.id || !this.audio_times) {
                return
            }
            try {
                this.saving = true
                await this.axios.patch(
                    `/api/articles/narration/${this.narration.id}/audio_times`,
                    { audio_times: this.audio_times }
                )
                this.$set(this.narration, 'audio_times', this.audio_times)
            } catch (error) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to save timestamps: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.saving = false
            }
        },
        approveNarration() {
            this.$emit('approve', true)
        },
        initializeAudioHighlighting() {
            // Generate audio times based on the narration prop
            if (this.narration.audio_times && typeof this.narration.audio_times == 'string') {
                this.audio_times = JSON.parse(this.narration.audio_times)
            } else if (this.narration.audio_times) {
                this.audio_times = structuredClone(this.narration.audio_times)
            }

            this.audio_player = this.$refs['audio-player']
        },
        startTick() {
            this.audio_interval = window.setInterval(this.tick, 16)
        },
        endTick() {
            if (this.audio_interval) {
                window.clearInterval(this.audio_interval)
            }
        },
        tick() {
            let currentTime = null

            if (this.audio_player.currentTime) {
                currentTime = Math.floor(this.audio_player.currentTime * 1000)
            }

            this.removeCurrentWordHighlight()

            if (!this.audio_player.paused && this.play_until && this.audio_player.currentTime >= this.play_until) {
                this.audio_player.pause()
                this.play_until = null
            }

            // Find when we've hit the next word to highlight
            if (this.audio_times) {
                for (let i = 1; i < this.audio_times.length; i++) {
                    if (this.audio_times[i].time > currentTime) {
                        this.highlightWordByTokenId(i - 1)
                        break
                    }
                }
            }
        },
        checkTokenClicked(event) {
            // If clicking on the currently highlighted word, play the audio
            if (
                this.audio_player &&
                event.target && event.target.id == 'highlight'
            ) {
                this.audio_player.play()
            }
            // Otherwise, just select the currently highlighted word. Let the audio player continue to play if it is already.
            else if (event.target && event.target.className.match(/t\d+/g)) {
                let tokenNumber = event.target.className.match(/t\d+/g)[0].substring(1)
                let newTime = null

                if (
                    this.audio_player &&
                    this.audio_times
                ) {
                    newTime = this.audio_times[tokenNumber].time / 1000
                    this.audio_player.currentTime = newTime
                }

                this.removeCurrentWordHighlight()

                this.highlightWordByTokenId(tokenNumber)
            }
        },
        removeCurrentWordHighlight() {
            let currentWord = document.getElementById('highlight')
            if (currentWord) {
                currentWord.removeAttribute('id')
            }
        },
        highlightWordByTokenId(tokenId) {
            const nextWord = document.getElementsByClassName('t' + tokenId)[0]
            if (nextWord) {
                nextWord.setAttribute('id', 'highlight')
                if (this.follow_highlight) {
                    nextWord.scrollIntoView({block: 'center'})
                }
            }
        },
        adjustTime(time, index) {
            if (this.audio_times[index] === undefined) {
                return
            }
            this.audio_times[index].time = String(time)

        },
        adjustWord(word, index) {
            if (this.audio_times[index] === undefined) {
                return
            }
            this.audio_times[index].word = word
        },
        removeWord(index) {
            if (this.audio_times[index] === undefined) {
                return
            }
            this.audio_times.splice(index, 1)
        },
        insertWord(index) {
            if (this.audio_times[index] === undefined) {
                return
            }
            this.audio_times.splice(index + 1, 0, {
                time: '',
                word: '',
            })
        },
        jumpToTime(index, playIfPaused = false, stopAfter = false) {
            if (this.audio_times[index] === undefined) {
                return
            }
            this.audio_player.currentTime = this.audio_times[index].time / 1000
            this.removeCurrentWordHighlight()
            this.highlightWordByTokenId(index)

            if (playIfPaused && this.audio_player.paused) {
                this.audio_player.play()
            }
            this.play_until = null
            if (stopAfter && this.audio_times[index + 1] !== undefined) {
                this.play_until = this.audio_times[index + 1].time / 1000
            }
        },
        stopOnExit() {
            /* stops audio on exit, resets the player's position
             * and removes highlighting from the current word
             */

            if (this.audio_player) {
                this.audio_player.pause()
                this.audio_player.currentTime = 0
            }

            this.removeCurrentWordHighlight()
        },
        cancel(event) {
            event.preventDefault()

            this.hide()
        },
        hide() {
            this.$emit('hide')
        },
        hidden() {
            this.stopOnExit()
            this.endTick()
            this.audio_times = null
            this.edit_mode = false
            this.saving = false
            this.follow_highlight = false

            this.$emit('hide')
        },
    }
}
</script>
